import { BellRing, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusChip, adminButton } from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import {
  FOLLOW_UP_QUICK_PICKS,
  LEAD_SOURCES,
  followUpLabel,
  followUpState,
  leadSourceOf,
  quickPickDate,
  type LeadSource,
} from '@/lib/follow-ups';

type Lead = { follow_up_on?: string | null; status: string };

/** Small chip for list rows: "Due today", "Overdue · 2 days" or the date. */
export function FollowUpBadge({ lead, className }: { lead: Lead; className?: string }) {
  const label = followUpLabel(lead);
  const state = followUpState(lead);
  if (!label || state === 'none') return null;
  return (
    <StatusChip
      tone={state === 'overdue' ? 'rose' : state === 'due_today' ? 'amber' : 'grey'}
      dot={false}
      className={cn('gap-1 whitespace-nowrap', className)}
      title="Follow up on"
    >
      <BellRing className="h-3 w-3" aria-hidden="true" />
      {label}
    </StatusChip>
  );
}

/** "Follow up on" quick picks + date, for the enquiry details dialog. */
export function FollowUpPicker({
  value,
  disabled,
  onChange,
  idPrefix = 'follow-up',
}: {
  value: string | null | undefined;
  disabled?: boolean;
  onChange: (date: string | null) => void;
  idPrefix?: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}-date`}>Follow up on</Label>
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Follow-up quick picks">
        {FOLLOW_UP_QUICK_PICKS.map((pick) => {
          const date = quickPickDate(pick.key);
          return (
            <Button
              key={pick.key}
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              aria-pressed={value === date}
              className={cn(adminButton.outline, 'h-8 px-3 text-[12.5px]', value === date && 'border-primary text-primary')}
              onClick={() => onChange(date)}
            >
              {pick.label}
            </Button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <Input
          id={`${idPrefix}-date`}
          type="date"
          className="h-9 sm:max-w-[180px]"
          disabled={disabled}
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value || null)}
        />
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            className="h-9 gap-1 text-[12.5px] text-muted-foreground"
            onClick={() => onChange(null)}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}

export function LeadSourceSelect({
  value,
  disabled,
  onChange,
  id = 'lead-source',
}: {
  value: string | null | undefined;
  disabled?: boolean;
  onChange: (source: LeadSource) => void;
  id?: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Lead source</Label>
      <Select value={leadSourceOf(value)} disabled={disabled} onValueChange={(v) => onChange(v as LeadSource)}>
        <SelectTrigger id={id} className="h-9 sm:max-w-[220px]" aria-label="Lead source">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LEAD_SOURCES.map((s) => (
            <SelectItem key={s.value} value={s.value}>
              {s.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
